import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2, Globe, Mail, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { listTime } from "@/components/whatsapp/chatUtils";

const SOURCE = (kind) => {
  if (kind.startsWith("site_down")) return "الموقع / SEO";
  if (kind.startsWith("http500")) return "الخادم";
  if (kind.startsWith("voice") || kind === "no_voices") return "الأصوات";
  if (kind.startsWith("gen_failed")) return "التوليد";
  if (kind.startsWith("social")) return "قنوات التواصل";
  if (kind.startsWith("wa_")) return "واتساب بوت";
  if (kind.startsWith("email_")) return "إيميل بوت";
  return "عام";
};

const Row = ({ g, onResolve }) => (
  <div className={`rounded-xl border p-3 flex items-start gap-3 ${g.resolved ? "border-border opacity-70" : "border-red-500/40 bg-red-500/5"}`} data-testid={`ops-alert-${g.kind}`} dir="auto">
    <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${g.resolved ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"}`}>
      {g.resolved ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-sm">{g.title}</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{SOURCE(g.kind)}</span>
        {g.today > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 font-bold" data-testid={`ops-today-${g.kind}`}>اليوم ×{g.today}</span>}
        {g.count > g.today && <span className="text-[10px] text-muted-foreground">إجمالي ×{g.count}</span>}
      </div>
      {g.detail && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{g.detail}</p>}
      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
        <span>آخر حدوث: {listTime(g.last_at)}</span>
        {g.customers.length > 0 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> العملاء المتأثرون: <bdi>{g.customers.join("، ")}</bdi></span>}
        {g.resolved && g.resolved_at && <span className="text-emerald-600">عولج {listTime(g.resolved_at)}</span>}
      </div>
    </div>
    {!g.resolved && <Button size="sm" variant="outline" className="shrink-0 text-xs" onClick={() => onResolve(g.kind)} data-testid={`ops-resolve-${g.kind}`}>تمت المعالجة</Button>}
  </div>
);

export const AdminOpsAlerts = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/ops-alerts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const resolve = async (kind) => { try { await api.post(`/admin/ops-alerts/${kind}/resolve`); toast.success("تم تعليم العطل كمعالَج"); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const checkSite = async () => {
    setBusy(true);
    try { const { data: d } = await api.post("/admin/ops-alerts/check-site"); const bad = d.results.filter((r) => !r.ok); toast[bad.length ? "error" : "success"](bad.length ? `${bad.length} صفحة لا تعمل` : "كل صفحات الموقع تعمل ✅"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return <div className="glass rounded-2xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  const site = data.site?.results || [];
  const siteBad = site.filter((r) => !r.ok).length;
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="ops-alerts-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-600" /> أعطال اليوم</h2>
          <p className="text-xs text-muted-foreground mt-1">كل عطل يقطع تجربة العملاء (الخادم، الموقع وSEO، التوليد، الأصوات، البوتات، النشر) يصل هنا وإلى الجرس وبريد <bdi>{data.ops_email}</bdi> فورًا.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={checkSite} disabled={busy} data-testid="ops-check-site"><Globe className="w-3.5 h-3.5" /> {busy ? "جارٍ الفحص…" : "افحص الموقع الآن"}</Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="ops-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border p-3 text-center"><div className={`text-2xl font-extrabold ${data.today ? "text-red-600" : "text-emerald-600"}`} data-testid="ops-today-count">{data.today}</div><div className="text-[11px] text-muted-foreground">عطل اليوم</div></div>
        <div className="rounded-xl border border-border p-3 text-center"><div className={`text-2xl font-extrabold ${data.open ? "text-amber-600" : "text-emerald-600"}`} data-testid="ops-open-count">{data.open}</div><div className="text-[11px] text-muted-foreground">مفتوح بلا معالجة</div></div>
        <div className="rounded-xl border border-border p-3 text-center"><div className={`text-2xl font-extrabold ${siteBad ? "text-red-600" : "text-emerald-600"}`} data-testid="ops-site-count">{site.length ? `${site.length - siteBad}/${site.length}` : "—"}</div><div className="text-[11px] text-muted-foreground">صفحات الموقع تعمل{data.site?.at ? ` · ${listTime(data.site.at)}` : ""}</div></div>
      </div>
      {site.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ops-site-pages">
          {site.map((r) => <span key={r.path} title={r.error} className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${r.ok ? "bg-emerald-500/10 text-emerald-700" : "bg-red-500/15 text-red-700 font-bold"}`} dir="ltr">{r.ok ? "✓" : "✗"} {r.path}</span>)}
        </div>
      )}
      {data.groups.length === 0
        ? <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2" data-testid="ops-empty"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> لا أعطال خلال آخر 7 أيام</div>
        : <div className="space-y-2">{data.groups.map((g) => <Row key={g.kind} g={g} onResolve={resolve} />)}</div>}
    </div>
  );
};
